import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CardHeader,
  IconButton,
  Button,
  Popover,
  Box,
  TextField,
  Checkbox,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
} from "@mui/material";
import {
  Print,
  Visibility,
  ExpandMore,
  ExpandLess,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import config from "../../../config";
import { generatePdf } from "../Sale/SalesOrderPrint";
import {
  getDashboardCounts,
  SalesOrderListApi,
  ApproveSo,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";

// === Card Icons ===
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

export default function Dashboard() {
  const [counts, setCounts] = useState({});
  const [salesOrders, setSalesOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [soToApprove, setSoToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [showRemarkForOrderId, setShowRemarkForOrderId] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "Pending",
  });

  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedSoId, setExpandedSoId] = useState(null);

  const userRole = localStorage.getItem("role");

  // ── Theme-aware helpers ──
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.18)
    : alpha(theme.palette.primary.main, 0.08);

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#fafafa"
      : theme.palette.background.paper;

  const getStatusColor = (status) => {
    if (status === "Approved") return "success";
    if (status === "Rejected") return "error";
    return "warning";
  };

  const getStatusBorderColor = (status) => {
    if (status === "Approved") return theme.palette.success.main;
    if (status === "Rejected") return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchOrders(1, true, filters);
    }, 400);
    return () => clearTimeout(handler);
  }, [filters]);

  const fetchCounts = async () => {
    try {
      const res = await getDashboardCounts();
      setCounts(res || {});
    } catch (err) {
      console.error("Failed to fetch dashboard counts", err);
    }
  };

  const fetchOrders = async (
    pageNum = 1,
    reset = false,
    filterValues = filters
  ) => {
    const apiFilters = {
      status: filterValues.status === "All" ? "" : filterValues.status,
      project_name: filterValues.search || "",
      client_name: filterValues.search || "",
      company_name: filterValues.search || "",
    };
    const data = await SalesOrderListApi(pageNum, apiFilters);
    if (reset || pageNum === 1) {
      setSalesOrders(data?.results || []);
    } else {
      setSalesOrders((prev) => [...prev, ...(data?.results || [])]);
    }
    setHasMore(data?.next !== null && Boolean(data?.next));
    setPage(pageNum);
  };

  const fetchMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchOrders(nextPage, false, filters);
  };

  const handleApprove = async (order, status, remarks = "") => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }
    try {
      const updated = await ApproveSo(
        order.id,
        approvedById,
        order.status,
        remarks
      );
      setSalesOrders((prev) =>
        prev.map((item) => (item.id === order.id ? updated : item))
      );
      fetchCounts();
      toast.success(
        updated.status === "Approved"
          ? "Sales Order (MOS) approved successfully!"
          : "Sales Order (MOS) status updated!"
      );
    } catch (error) {
      console.error("Approval Toggle Error:", error);
      toast.error("Failed to update status.");
    }
  };

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({ ...prev, status }));
  };

  const cardData = [
    // MOS Cards
    {
      title: "Total MOS",
      subtitle: "All Sales Orders",
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      value: counts.projects?.total || 0,
      color: theme.palette.primary.main,
      action: () => handleStatusFilter("All"),
    },
    {
      title: "Approved MOS",
      subtitle: "In Production / Active",
      icon: <BarChartIcon sx={{ fontSize: 28 }} />,
      value: counts.projects?.approved || 0,
      color: theme.palette.success.main,
      action: () => handleStatusFilter("Approved"),
    },
    {
      title: "Pending MOS",
      subtitle: "Awaiting Approval",
      icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
      value: counts.projects?.pending || 0,
      color: theme.palette.warning.main,
      action: () => handleStatusFilter("Pending"),
    },
    {
      title: "Today's MOS",
      subtitle: "Booked Today",
      icon: <ShoppingCartIcon sx={{ fontSize: 28 }} />,
      value: counts.projects?.today || 0,
      color: theme.palette.info.main,
      action: () => navigate("/dashboard/saleorderlist"),
    },
    // PI Cards
    {
      title: "Total PI",
      subtitle: "Proforma Invoices",
      icon: <ReceiptLongIcon sx={{ fontSize: 28 }} />,
      value: counts.proforma_invoices?.total || 0,
      color: theme.palette.secondary.main,
      action: () => navigate("/dashboard/performatable"),
    },
    {
      title: "Approved PI",
      subtitle: "Confirmed Orders",
      icon: <InventoryIcon sx={{ fontSize: 28 }} />,
      value: counts.proforma_invoices?.approved || 0,
      color: theme.palette.success.dark,
      action: () => navigate("/dashboard/performatable"),
    },
    {
      title: "Pending PI",
      subtitle: "Quoted / Pending",
      icon: <CheckCircleIcon sx={{ fontSize: 28 }} />,
      value: counts.proforma_invoices?.pending || 0,
      color: theme.palette.error.main,
      action: () => navigate("/dashboard/performatable"),
    },
    {
      title: "Today's PI",
      subtitle: "Generated Today",
      icon: <ThumbUpIcon sx={{ fontSize: 28 }} />,
      value: counts.proforma_invoices?.today || 0,
      color: theme.palette.primary.dark,
      action: () => navigate("/dashboard/performatable"),
    },
  ];

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  // ── MOBILE CARD ──
  const MobileSoCard = ({ order, index }) => {
    const isExpanded = expandedSoId === order.id;

    return (
      <Box key={order.id || index}>
        <Box
          onClick={() => setExpandedSoId(isExpanded ? null : order.id)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: rowBg(index),
            borderLeft: `4px solid ${getStatusBorderColor(order.status)}`,
            transition: "background-color 0.15s ease",
            "&:active": { bgcolor: "action.selected" },
          }}
        >
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "text.disabled",
                mb: 0.3,
              }}
            >
              #{index + 1} • {formatOnlyDate(order.created_at)} • SO #{order.so_no}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.85rem",
                mb: 0.1,
                color: "text.primary",
              }}
            >
              {order.project_name || "-"}
            </Typography>
            <Typography
              sx={{ fontSize: "0.75rem", color: "text.secondary" }}
            >
              {order.party_details?.company_name || order.party_details?.name || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            {userRole !== "Staff" && (
              <Checkbox
                checked={order.status === "Approved" || order.status === "Rejected"}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setApproveAnchorEl(e.currentTarget);
                  setSoToApprove(order);
                }}
                sx={{
                  padding: 0,
                  transform: "scale(0.85)",
                  color:
                    order.status === "Approved"
                      ? "success.main"
                      : order.status === "Rejected"
                      ? "error.main"
                      : "action.disabled",
                  "&.Mui-checked": {
                    color:
                      order.status === "Approved"
                        ? "success.main"
                        : "error.main",
                  },
                }}
              />
            )}

            <Chip
              label={order.status || "Pending"}
              color={getStatusColor(order.status)}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
              }}
            />

            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setExpandedSoId(isExpanded ? null : order.id);
              }}
            >
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        {/* Expanded Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1.2,
              bgcolor: isDark
                ? alpha(theme.palette.common.white, 0.04)
                : alpha(theme.palette.primary.main, 0.04),
              borderLeft: `4px solid ${getStatusBorderColor(order.status)}`,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              {[
                { label: "CONTACT PERSON", value: order.party_details?.name || "-" },
                { label: "CONTACT NO.", value: order.party_details?.contact || "-" },
                { label: "EMAIL", value: order.party_details?.email || "-", wordBreak: true },
                { label: "APPROVED BY", value: order.approved_name || "-" },
                { label: "GST", value: order.party_details?.gst || "-" },
                { label: "PAN", value: order.party_details?.pan || "-" },
              ].map(({ label, value, wordBreak }) => (
                <Box key={label}>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "text.disabled",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      color: "text.secondary",
                      wordBreak: wordBreak ? "break-all" : "normal",
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  ADDRESS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    lineHeight: 1.3,
                  }}
                >
                  {order.party_details?.address || "-"}
                </Typography>
              </Box>

              {order.remarks && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "text.disabled",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 600,
                    }}
                  >
                    REMARKS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      lineHeight: 1.3,
                    }}
                  >
                    {order.remarks}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 1, borderColor: "divider" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.8,
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  generatePdf(order);
                }}
                sx={{
                  bgcolor: isDark
                    ? alpha(theme.palette.primary.main, 0.15)
                    : "#e3f2fd",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    bgcolor: isDark
                      ? alpha(theme.palette.primary.main, 0.28)
                      : "#bbdefb",
                  },
                }}
              >
                <Print sx={{ fontSize: "1.1rem", color: "primary.main" }} />
              </IconButton>

              {order.project && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`${order.project}`, "_blank");
                  }}
                  sx={{
                    bgcolor: isDark ? alpha("#7b1fa2", 0.15) : "#f3e5f5",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: isDark ? alpha("#7b1fa2", 0.28) : "#e1bee7",
                    },
                  }}
                >
                  <Visibility sx={{ fontSize: "1.1rem", color: "#7b1fa2" }} />
                </IconButton>
              )}
            </Box>
          </Box>
          <Divider />
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 0.8, sm: 1.2 },
        bgcolor: "background.default",
        height: "calc(100vh - 75px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      {/* ── TOP COMPACT KPI METRICS STRIP (FIXED / NON-SCROLLING) ── */}
      <Grid container spacing={1} sx={{ mb: 0.8, flexShrink: 0 }}>
        {cardData.map((card, index) => (
          <Grid item xs={6} sm={3} md={1.5} key={index}>
            <Card
              elevation={0}
              onClick={card.action}
              sx={{
                p: "5px 8px",
                border: `1px solid ${theme.palette.divider}`,
                borderLeft: `3.5px solid ${card.color}`,
                bgcolor: theme.palette.background.paper,
                borderRadius: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: 48,
                transition: "all 0.15s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: isDark
                    ? "0 4px 12px rgba(0,0,0,0.4)"
                    : "0 4px 12px rgba(0,0,0,0.06)",
                  borderColor: card.color,
                },
              }}
            >
              <Box sx={{ overflow: "hidden", pr: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.64rem",
                    fontWeight: 600,
                    lineHeight: 1.1,
                    display: "block",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="text.primary"
                  sx={{ fontSize: "1.02rem", lineHeight: 1.2, mt: 0.1 }}
                >
                  {card.value}
                </Typography>
              </Box>
              <Box
                sx={{
                  color: card.color,
                  bgcolor: alpha(card.color, isDark ? 0.18 : 0.1),
                  p: 0.5,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(card.icon, { sx: { fontSize: 16 } })}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── FILTER & ACTION STRIP (FIXED / NON-SCROLLING) ── */}
      <Box
        sx={{
          mb: 0.8,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          p: "6px 10px",
          bgcolor: "background.paper",
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ fontSize: "0.82rem", mr: 0.5 }}>
            Sales Orders (MOS) — {filters.status || "All"}
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={filters.status}
            exclusive
            onChange={(e, val) => {
              if (val !== null) setFilters((prev) => ({ ...prev, status: val }));
            }}
            sx={{
              height: 28,
              "& .MuiToggleButton-root": {
                fontSize: "0.66rem",
                px: 1,
                py: 0,
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <ToggleButton value="Pending">Pending</ToggleButton>
            <ToggleButton value="Approved">Approved</ToggleButton>
            <ToggleButton value="Rejected">Rejected</ToggleButton>
            <ToggleButton value="All">All</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search MOS / Client / Company..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: filters.search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilters((prev) => ({ ...prev, search: "" }))}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiInputBase-root": { height: 28, fontSize: "0.72rem" },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
            onClick={() => {
              fetchCounts();
              fetchOrders(1, true, filters);
            }}
            sx={{ textTransform: "none", fontSize: "0.72rem", height: 28, px: 1 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ── SALES ORDER LIST TABLE CONTAINER (ONLY ROWS SCROLL) ── */}
      <Box
        sx={{
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {isMobile ? (
          /* ── MOBILE CARDS (INTERNAL SCROLL) ── */
          <Box
            id="scrollableMobileCardsContainer"
            sx={{
              p: 0.5,
              flex: 1,
              overflowY: "auto",
              bgcolor: "background.paper",
            }}
          >
            <InfiniteScroll
              dataLength={salesOrders.length}
              next={fetchMoreData}
              hasMore={hasMore}
              scrollableTarget="scrollableMobileCardsContainer"
              loader={
                <Typography textAlign="center" p={1.5} color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                  Loading more orders...
                </Typography>
              }
            >
              {salesOrders.length > 0 ? (
                salesOrders.map((order, index) => (
                  <MobileSoCard
                    key={order.id || index}
                    order={order}
                    index={index}
                  />
                ))
              ) : (
                <Typography textAlign="center" p={3} color="text.secondary">
                  No Sales Orders found
                </Typography>
              )}
            </InfiniteScroll>
          </Box>
        ) : (
          /* ── DESKTOP TABLE (INTERNAL SCROLL WITH STICKY HEADER) ── */
          <TableContainer
            id="scrollableDesktopTableContainer"
            sx={{
              flex: 1,
              height: "100%",
              overflowY: "auto",
              bgcolor: "background.paper",
            }}
          >
            <InfiniteScroll
              dataLength={salesOrders.length}
              next={fetchMoreData}
              hasMore={hasMore}
              scrollableTarget="scrollableDesktopTableContainer"
              loader={
                <Typography textAlign="center" p={1.5} color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                  Loading more orders...
                </Typography>
              }
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1000,
                  "& td, & th": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        color: "text.primary",
                        backgroundColor: `${tableHeadBg} !important`,
                        fontSize: "0.75rem",
                        py: 0.8,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      },
                    }}
                  >
                    {userRole !== "Staff" && <TableCell align="center">Approve</TableCell>}
                    <TableCell align="center">S.No.</TableCell>
                    <TableCell align="center">SO / MOS No.</TableCell>
                    <TableCell align="center">Date</TableCell>
                    <TableCell align="center">Project Name</TableCell>
                    <TableCell align="center">Client / Contact</TableCell>
                    <TableCell align="center">Company Name</TableCell>
                    <TableCell align="center">Contact No.</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Approved By</TableCell>
                    <TableCell align="center">Remarks</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {salesOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        No Sales Orders found matching current filter
                      </TableCell>
                    </TableRow>
                  ) : (
                    salesOrders.map((order, idx) => (
                      <TableRow
                        hover
                        key={order.id || idx}
                        sx={{
                          backgroundColor: rowBg(idx),
                          "&:hover": {
                            backgroundColor: isDark
                              ? `${alpha(theme.palette.primary.main, 0.1)} !important`
                              : "#e3f2fd !important",
                          },
                          "& td": {
                            py: 0.6,
                            px: 1,
                            fontSize: "0.78rem",
                          },
                        }}
                      >
                        {userRole !== "Staff" && (
                          <TableCell align="center">
                            <Checkbox
                              checked={order.status === "Approved" || order.status === "Rejected"}
                              onChange={(e) => {
                                setApproveAnchorEl(e.currentTarget);
                                setSoToApprove(order);
                              }}
                              sx={{
                                p: 0,
                                transform: "scale(0.85)",
                                color:
                                  order.status === "Approved"
                                    ? "success.main"
                                    : order.status === "Rejected"
                                    ? "error.main"
                                    : "action.disabled",
                                "&.Mui-checked": {
                                  color:
                                    order.status === "Approved"
                                      ? "success.main"
                                      : "error.main",
                                },
                              }}
                            />
                          </TableCell>
                        )}

                        <TableCell align="center">{idx + 1}</TableCell>

                        <TableCell align="center" sx={{ fontWeight: 600, color: "primary.main" }}>
                          {order.so_no}
                        </TableCell>

                        <TableCell align="center">{formatOnlyDate(order.created_at)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 500 }}>
                          {order.project_name || "-"}
                        </TableCell>
                        <TableCell align="center">{order.party_details?.name || "-"}</TableCell>
                        <TableCell align="center">{order.party_details?.company_name || "-"}</TableCell>
                        <TableCell align="center">{order.party_details?.contact || "-"}</TableCell>

                        <TableCell align="center">
                          <Chip
                            label={order.status || "Pending"}
                            color={getStatusColor(order.status)}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">{order.approved_name || "-"}</TableCell>
                        <TableCell align="center">{order.remarks || "-"}</TableCell>

                        <TableCell align="center">
                          <Tooltip title="Print / PDF">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => generatePdf(order)}
                              sx={{ p: 0.5 }}
                            >
                              <Print fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {order.project && (
                            <Tooltip title="View Attachment">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => window.open(`${order.project}`, "_blank")}
                                sx={{ p: 0.5 }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        )}
      </Box>

      {/* ── Approve/Reject Popover ── */}
      <Popover
        open={Boolean(approveAnchorEl)}
        anchorEl={approveAnchorEl}
        onClose={() => {
          setApproveAnchorEl(null);
          setSoToApprove(null);
          setRemarkInput("");
          setShowRemarkForOrderId(null);
          setActionType(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            p: 2,
            minWidth: 260,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box>
          {showRemarkForOrderId === soToApprove?.id ? (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: "text.primary", fontWeight: 500 }}>
                Enter remark for {actionType === "Approved" ? "Approval" : "Rejection"}:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={remarkInput}
                onChange={(e) => setRemarkInput(e.target.value)}
                placeholder="Enter remark..."
                sx={{ mb: 2, "& .MuiInputBase-root": { fontSize: "0.75rem" } }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setSoToApprove(null);
                    setRemarkInput("");
                    setShowRemarkForOrderId(null);
                    setActionType(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  color={actionType === "Approved" ? "success" : "error"}
                  variant="contained"
                  size="small"
                  onClick={() => {
                    if (!remarkInput.trim()) {
                      toast.error("Remark is required!");
                      return;
                    }
                    handleApprove(
                      soToApprove,
                      actionType,
                      remarkInput
                    );
                    setApproveAnchorEl(null);
                    setSoToApprove(null);
                    setRemarkInput("");
                    setShowRemarkForOrderId(null);
                    setActionType(null);
                  }}
                >
                  Confirm {actionType}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5, color: "text.primary", fontWeight: 500 }}>
                Update status for MOS #{soToApprove?.so_no}?
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Button
                  color="success"
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => {
                    setShowRemarkForOrderId(soToApprove?.id);
                    setActionType("Approved");
                  }}
                >
                  Approve
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => {
                    setShowRemarkForOrderId(soToApprove?.id);
                    setActionType("Rejected");
                  }}
                >
                  Reject
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
}